import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  TextInput,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
} from './changePasswordModal.styles';
import { updateEmail, resendEmailVerificationCode, verifyEmail } from '../../../services/auth';
import { useAuth } from '../../../hooks/useAuth';

const UpdateEmailModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('email'); // 'email' | 'verify'
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const updateEmailMutation = useMutation({
    mutationFn: updateEmail,
    onSuccess: (_, variables) => {
      setErrors({});
      window.alert(
        `Your email has been changed to ${variables.email}. Please use this new email to log in from now on.`
      );
      setStep('verify');
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to update email. Please try again.' });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: verifyEmail,
    onSuccess: () => {
      window.alert('Email verified successfully. Please log in again with your new email.');
      forceLogoutAndClose();
    },
    onError: (err) => {
      setErrors({ form: err?.detail || 'Verification failed. Please check your code.' });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendEmailVerificationCode,
    onSuccess: () => {
      window.alert('Verification code sent! Please check your email.');
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to resend code. Please try again.' });
    },
  });

  const resetState = () => {
    setStep('email');
    setNewEmail('');
    setCode('');
    setErrors({});
    updateEmailMutation.reset();
    verifyMutation.reset();
    resendMutation.reset();
  };

  // Plain close — only reachable from the header "X" while still on step 1
  // (no email change made yet), so no need to force a logout here.
  const handleClose = () => {
    resetState();
    onClose();
  };

  // Used by: header "X" while on the verify step, the Skip button, and a
  // successful verify — all of these happen after the email has already
  // been changed server-side, so the session must be invalidated and the
  // user sent back to log in with the new email.
  const forceLogoutAndClose = () => {
    resetState();
    onClose();
    logout();
    navigate('/login');
  };

  const handleHeaderClose = () => {
    if (step === 'verify') {
      forceLogoutAndClose();
    } else {
      handleClose();
    }
  };

  const handleSkip = () => {
    forceLogoutAndClose();
  };

  const handleUpdateEmailSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!newEmail.trim()) {
      nextErrors.newEmail = 'New email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(newEmail.trim())) {
      nextErrors.newEmail = 'Enter a valid email address';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    updateEmailMutation.mutate({ email: newEmail.trim() });
  };

  const handleVerifySubmit = (e) => {
    e.preventDefault();

    if (!code.trim()) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    setErrors({});
    verifyMutation.mutate({ email: newEmail.trim(), code: code.trim() });
  };

  const handleResend = () => {
    setErrors({});
    resendMutation.mutate();
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleHeaderClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step]);

  if (!isOpen) return null;

  return createPortal(
    // No onClick handler here on purpose — clicking outside the modal must
    // NOT close it. Only the header "X" (and, on the verify step, Skip /
    // Verify) can dismiss it.
    <ModalOverlay
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      ref={overlayRef}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="modal-title">
            {step === 'email' ? 'Update email' : 'Verify new email'}
          </ModalTitle>
          <ModalCloseButton onClick={handleHeaderClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        {step === 'email' && (
          <form onSubmit={handleUpdateEmailSubmit}>
            <ModalBody>
              <FormGroup>
                <InputLabel htmlFor="new-email">New email</InputLabel>
                <TextInput
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  autoComplete="email"
                  $hasError={!!errors.newEmail}
                  aria-describedby={errors.newEmail ? 'new-email-error' : undefined}
                />
                {errors.newEmail && (
                  <ErrorText id="new-email-error" role="alert">
                    {errors.newEmail}
                  </ErrorText>
                )}
              </FormGroup>

              {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
            </ModalBody>

            <ModalFooter>
              <ModalCancelButton type="button" onClick={handleClose}>
                Cancel
              </ModalCancelButton>
              <ModalSubmitButton type="submit" disabled={updateEmailMutation.isPending}>
                {updateEmailMutation.isPending ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : null}
                {updateEmailMutation.isPending ? 'Updating…' : 'Update email'}
              </ModalSubmitButton>
            </ModalFooter>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifySubmit}>
            <ModalBody>
              <FormGroup>
                <InputLabel htmlFor="verify-email">Email</InputLabel>
                <TextInput id="verify-email" type="email" value={newEmail} disabled />
              </FormGroup>

              <FormGroup>
                <InputLabel htmlFor="verification-code">Verification code</InputLabel>
                <TextInput
                  id="verification-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter the code from your email"
                  $hasError={!!errors.code}
                  aria-describedby={errors.code ? 'verification-code-error' : undefined}
                />
                {errors.code && (
                  <ErrorText id="verification-code-error" role="alert">
                    {errors.code}
                  </ErrorText>
                )}
              </FormGroup>

              <div style={{ fontSize: '0.8125rem' }}>
                Didn&apos;t get a code?{' '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={!resendMutation.isPending ? handleResend : undefined}
                  onKeyDown={(e) =>
                    !resendMutation.isPending &&
                    (e.key === 'Enter' || e.key === ' ') &&
                    handleResend()
                  }
                  style={{
                    cursor: resendMutation.isPending ? 'default' : 'pointer',
                    opacity: resendMutation.isPending ? 0.6 : 1,
                    textDecoration: 'underline',
                  }}
                >
                  {resendMutation.isPending ? 'Sending…' : 'Resend code'}
                </span>
              </div>

              {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
            </ModalBody>

            <ModalFooter>
              <ModalCancelButton type="button" onClick={handleSkip}>
                Skip
              </ModalCancelButton>
              <ModalSubmitButton type="submit" disabled={verifyMutation.isPending}>
                {verifyMutation.isPending ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : null}
                {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
              </ModalSubmitButton>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default UpdateEmailModal;
